import React, { useEffect, useState } from "react";
import { Box, TextField, Autocomplete as MUIAutoComplete, Button, Typography, CircularProgress } from "@mui/material";
import { useForm } from "./../../context/FormContext";
import fetchRates from "./../../api/fetchRates";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import {
    getAddressComponentByType,
    desiredTypes,
} from "./../../utils/map-address.utils";

const myStyle = {
    background: "#6EA3D5",
    padding: "8px",
    borderTopLeftRadius: "5px",
    borderTopRightRadius: "5px",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
};

const libraries = ["places", "geometry"]; // Define the array outside the component

function ReceiverDoorDetailsForm({type}) {
    
    const { formData, updateField, errors } = useForm();
    const [isFormValid, setIsFormValid] = useState(false);

    const provinceData = [
        { id: "EC", label: "Eastern Cape" },
        { id: "FS", label: "Free State" },
        { id: "GP", label: "Gauteng" },
        { id: "KZN", label: "KwaZulu-Natal" },
        { id: "LP", label: "Limpopo" },
        { id: "MP", label: "Mpumalanga" },
        { id: "NW", label: "North West" },
        { id: "NC", label: "Northern Cape" },
        { id: "WC", label: "Western Cape" },
    ];
    
    const [bVarient, setBVarient] = useState("outlined");
    const [iVarient, setIVarient] = useState("contained");
    const [placeSelected, setPlaceSelected] = useState(true);
    const handleBusinessBtn = () => {
        updateField('addressType', "business")
        setBVarient("contained");
        setIVarient("outlined");
    };

    const handleIndividualBtn = () => {
        
        updateField('addressType', "residential")
        setIVarient("contained");
        setBVarient("outlined");
    };

    const fetchRatesData = async () =>  {
        try {
            updateField('parcelSizesLoading', true);
            const result = await fetchRates({
                collection_address: {
                    type: (type.includes('locker_')) ? 'locker' : ((type.includes('pp_')) ? 'pickup point' : 'kiosk')
                },
                delivery_address: {
                    street_address: formData.streetAddress,
                    local_area: formData.suburb,
                    suburb: formData.suburb,
                    city: formData.city,
                    code: formData.postCode,
                    zone: formData.province.id,
                    country: "South Africa",
                    entered_address: `${formData.streetAddress}, ${formData.suburb}, ${formData.city}, ${formData.postCode}, South Africa`,
                    type: "residential",
                }
            }, type.includes('pp_') ? 'point' : 'kiosk');
           
            updateField('parcelSizes', result);
            updateField('parcelErrorMessage', null);
        } catch (err) {
            updateField('parcelSizesLoading', false);
            updateField('parcelErrorMessage', err.message);
        } finally {
            updateField('parcelSizesLoading', false);
        }
    }

    useEffect(() => {
        setIsFormValid(hasAllValues({
            province: formData.province,
            postCode: formData.postCode,
            city: formData.city,
            suburb: formData.suburb,
            streetAddress: formData.streetAddress
        }));
        
    }, [formData]);

    const hasAllValues = (obj) => {
        return Object.values(obj).every(value => {
            if (value === null || value === undefined || value === '') {
                return false;
            }

            // If value is an object (like MUI Autocomplete), check if it has a valid selection
            if (typeof value === 'object') {
                return Object.keys(value).length > 0; // Ensures object isn't empty
            }

            return true;
        });
    };
    const [autocomplete, setAutocomplete] = useState(null);
    const autocompleteOnLoad = React.useCallback(function callback(autocompletedata) {
        setAutocomplete(autocompletedata);
    },[]);

    const getAddressComponents = (place) => {
        const address = place.formatted_address;
        const streetAddress1 = getAddressComponentByType(place, ["route"]);
        const streetAddress = getAddressComponentByType(place, desiredTypes);
        const city = getAddressComponentByType(place, [
            "locality",
            "administrative_area_level_2",
        ]);
        const suburb = getAddressComponentByType(place, [
            "sublocality",
            "sublocality_level_1",
        ]);
        const localArea = getAddressComponentByType(place, [
            "sublocality",
            "sublocality_level_1",
        ]);
        const province = getAddressComponentByType(place, [
            "administrative_area_level_1",
        ]);
        const country = getAddressComponentByType(place, ["country"]);
        const code = getAddressComponentByType(place, ["postal_code"]);

        return {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            name: place.name || "",
            city: city ?? suburb,
            suburb: suburb ?? city,
            localArea: localArea ?? city,
            streetAddress: streetAddress1 ?? streetAddress,
            formatted_address: address,
            code,
            country,
            province,
        };
    };

    const onPlaceSelect = () => {
        if (autocomplete !== null) {
            const _adresses = getAddressComponents(autocomplete.getPlace());
            updateField("hasUserSelectPlace",true);
            updateField("streetAddress",_adresses?.streetAddress ?? "");
            updateField("local_area",_adresses?.localArea ?? "");
            updateField("suburb",_adresses?.suburb ?? "");
            updateField("city",_adresses?.city ?? "");
            updateField("province",{
                id: _adresses?.province?.short_name ?? "",
                label: _adresses?.province?.long_name ?? "",
            });
            updateField("zone",{
                id: _adresses?.province?.short_name ?? "",
                label: _adresses?.province?.long_name ?? "",
            });
            updateField("postCode",_adresses?.code ?? "");
            updateField("country",_adresses?.country ?? "");
            updateField("entered_address",_adresses?.formatted_address ?? "");
            updateField("lat",_adresses?.lat ?? "");
            updateField("lng",_adresses?.lng ?? "");
        } else {
            updateField("hasUserSelectPlace",false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            <Typography variant="h6" style={myStyle} fontWeight={"bold"}>
                Delivery Address
            </Typography>
           {
                (type === 'kiosk_to_door') ? (
                    <Box sx={{ display: "flex", mb: 2, gap: 2 }}>
                        <Box sx={{ width: "49%" }}>
                            <Button
                                fullWidth
                                variant={iVarient}
                                sx={{ backgroundColor: iVarient === 'contained' ? '#1565C0' : '' }}
                                onClick={() => handleIndividualBtn()}
                            >
                                Residential
                            </Button>
                        </Box>
                        <Box sx={{ width: "49%" }}>
                            <Button
                                variant={bVarient}
                                fullWidth
                                sx={{ backgroundColor: bVarient === 'contained' ? '#1565C0' : '' }}
                                onClick={() => handleBusinessBtn()}
                            >
                                Business
                            </Button>
                        </Box>
                    </Box>
                ) : null
            }
            
            {formData.addressType === "business" && <TextField
                id="company_name"
                label="Company (Optional)"
                value={formData.companyName || ""}
                onChange={(e) => updateField('companyName',e.target.value)}
                fullWidth
            />}
            <TextField
                id="complex"
                label="Complex / Building"
                value={formData.complex || ""}
                onChange={(e) => updateField('complex', e.target.value)}
                fullWidth
            />

            {/* <TextField
                required
                id="receiver_street_address"
                label="Receiver Street Address"
                value={formData.streetAddress || ""}
                onChange={(e) => updateField('streetAddress', e.target.value)}
                fullWidth
            /> */}

            <LoadScript
                id="script-loader"
                libraries={libraries}
                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
            >
                <Box
                    sx={{
                        display: "grid",
                        
                    }}
                >
                    <Autocomplete
                        onLoad={autocompleteOnLoad}
                        onPlaceChanged={onPlaceSelect}
                        restrictions={{ country: ["za"] }}
                       
                        options={{
                            types: ["address"],
                            fields: [
                                "address_components",
                                "formatted_address",
                                "name",
                                "place_id",
                                "geometry",
                            ],
                        }}
                    >
                        <>
                            <TextField
                                type="text"
                                value={formData.entered_address || ''}
                                placeholder={"Address"}
                                fullWidth
                                variant="outlined"
                                name="entered_address"
                                onChange={(e) => {
                                    updateField('entered_address', e.target.value);
                                    updateField('hasUserSelectPlace', false);
                                }}
                                onBlur={() => {
                                    if (!formData.hasUserSelectPlace) {
                                        updateField('entered_address', '');
                                    }
                                }}
                                error={!!errors.entered_address}
                            />
                            {errors?.entered_address && (
                                <div style={{ color: "red", marginBottom: '3px' }} className="HelperText">
                                    {errors.entered_address}
                                </div>
                            )}
                            {errors?.hasUserSelectPlace && (
                                <div style={{ color: "red", marginBottom: '3px' }} className="HelperText">
                                    Please select an address from suggestions.
                                </div>
                            )}
                        </>
                    </Autocomplete>
                </Box>
            </LoadScript>

            <TextField
                required
                id="receiver_suburb"
                label="Receiver Suburb"
                value={formData.suburb || ""}
                onChange={(e) => updateField('suburb', e.target.value)}
                fullWidth
            />

            <TextField
                required
                id="receiver_city"
                label="Receiver City"
                value={formData.city || ""}
                onChange={(e) => updateField('city', e.target.value)}
                fullWidth
            />

            <MUIAutoComplete
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                required
                id="receiver_province"
                options={provinceData}
                value={formData.province || null}
               
                onChange={
                    (event, newValue) => updateField('province', newValue)
                }
                renderInput={(params) => (
                <TextField
                    {...params}
                    label="Province"
                    error={!!errors.province} // Show error if province error exists
                    helperText={errors.province || ''} // Display error message
                />
            )}
            />
            
            <TextField
                required
                id="receiver_post_code"
                label="Receiver Postal Code"
                value={formData.postCode || ""}
                onChange={(e) => updateField('postCode', e.target.value)}
                fullWidth
            />

            <TextField
                id="delivery_instuction"
                label="Delivery Instruction"
                value={formData.deliveryInstruction || ""}
                onChange={(e) => updateField('deliveryInstruction', e.target.value)}
                fullWidth
            />
            {(type.includes('pp_')) && (
                <React.Fragment>
                    <Box sx={{ marginBottom: '8px', marginTop: '8px' }}>
                        <span className="text-gray-500 font-semibold">Note: The delivery address must be validated before selecting the parcel size.</span>
                    </Box> 
                    <Button  variant="contained" onClick={() => fetchRatesData()} color="success">
                        {formData?.parcelSizesLoading ? (
                            <CircularProgress size="25px" className="success" color="primary" />
                        ) : (
                            "Check available parcel sizes"
                        )}
                    </Button>
                </React.Fragment>
            )}
        </Box>
    );
}

export default ReceiverDoorDetailsForm;
